/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { initialAnalyticsData, StatusType } from '@constants/Marketing'
import {
  CampaignMessageStatus,
  marketingOverviewFilter,
  IAnalyticsData,
  ICampaignMessage,
} from '@dataTypes/marketing.type'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { setOverviewDateFilter } from '@store/reducers/marketing.reducer'
import { getDateRange, isDateInRange } from '@utils/date.utils'
import { getCampaignNameList, getErrorTypeList } from '@utils/filter.util'
import { calculatePercentageAndStatus } from '@utils/marketing.utils'
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

interface DropDownButtonItem {
  id: string
  text: string
}

interface CampaignOverviewHooks {
  bounced: IAnalyticsData
  campaignDropdownOptions: string[]
  dateFilter: marketingOverviewFilter
  delivered: IAnalyticsData
  deliveryRate: IAnalyticsData
  filteredMessages: ICampaignMessage[]
  filterSelectionList: {
    campaignName: string[]
    errorType: string[]
  }
  handleRefresh: () => void
  messageSent: IAnalyticsData
  onDropDownSelect: (e: any) => void
  onHideBouncedModal: () => void
  replies: IAnalyticsData
  scheduled: IAnalyticsData
  selectedCampaign: DropDownButtonItem
  setDateFilter: (dateFilter: marketingOverviewFilter) => void
  showBouncedModal: boolean
  onClickBouncedAnalytics: () => void
}

const useCampaignOverview = (
  setSelectedDropdownCampaignId?: Dispatch<SetStateAction<string>>,
  controlledDateFilter?: marketingOverviewFilter,
  controlledSetDateFilter?: (dateFilter: marketingOverviewFilter) => void,
): CampaignOverviewHooks => {
  const dispatch = useAppDispatch()
  const campaigns = useAppSelector((state) => state.marketing.campaigns)
  const selectedDate = useAppSelector((state) => state.marketing.selectedDate)

  const [filteredMessages, setFilteredMessages] = useState<ICampaignMessage[]>(
    [],
  )
  const [selectedCampaign, setSelectedCampaign] = useState<DropDownButtonItem>({
    id: '',
    text: 'Select campaign',
  })
  const internalDateFilter = useAppSelector(
    (state) => state.marketing.overviewDateFilter,
  )

  const internalSetDateFilter = (dateFilter: marketingOverviewFilter): void => {
    dispatch(setOverviewDateFilter(dateFilter))
  }

  const dateFilter = controlledDateFilter ?? internalDateFilter
  const setDateFilter = controlledSetDateFilter ?? internalSetDateFilter
  const [messageSent, setMessageSent] =
    useState<IAnalyticsData>(initialAnalyticsData)
  const [delivered, setDelivered] =
    useState<IAnalyticsData>(initialAnalyticsData)
  const [deliveryRate, setDeliveryRate] =
    useState<IAnalyticsData>(initialAnalyticsData)
  const [bounced, setBounced] = useState<IAnalyticsData>(initialAnalyticsData)
  const [replies, setReplies] = useState<IAnalyticsData>(initialAnalyticsData)
  const [scheduled, setScheduled] =
    useState<IAnalyticsData>(initialAnalyticsData)
  const [showBouncedModal, setShowBouncedModal] = useState<boolean>(false)

  const campaignDropdownOptions = useMemo(() => {
    const response: string[] = ['Select all', ...campaigns.map((c) => c.name)]
    return response
  }, [campaigns])

  const calculateCampaignOverview = (): void => {
    const dateRange = getDateRange(dateFilter, new Date(selectedDate))
    const selectedCampaignId = selectedCampaign.id ?? ''

    const _campaigns =
      selectedCampaignId.length > 0
        ? campaigns.filter((c) => c.campaign_id === selectedCampaignId)
        : campaigns

    setFilteredMessages(
      _campaigns
        .flatMap((c) =>
          c.campaign_message.map((m) => ({ ...m, campaign_name: c.name })),
        )
        .filter((m) => m.error_code || m.error_code?.length === 0),
    )

    const currentDeliveredCount = _campaigns.reduce((acc, campaign) => {
      return (
        acc +
        campaign.campaign_message.filter(
          (m) =>
            m.status === CampaignMessageStatus.sent &&
            m.message_type === 'Outgoing' &&
            m.date_time_sent &&
            isDateInRange(
              m.date_time_sent,
              dateRange.startCurrent,
              dateRange.endCurrent,
            ),
        ).length
      )
    }, 0)

    const prevDeliveredCount = _campaigns.reduce((acc, campaign) => {
      return (
        acc +
        campaign.campaign_message.filter(
          (m) =>
            m.status === CampaignMessageStatus.sent &&
            m.message_type === 'Outgoing' &&
            m.date_time_sent &&
            isDateInRange(
              m.date_time_sent,
              dateRange.startPrev,
              dateRange.endPrev,
            ),
        ).length
      )
    }, 0)

    const currentBouncedCount = _campaigns.reduce(
      (acc, campaign) =>
        acc +
        campaign.campaign_message.filter(
          (m) =>
            m.status === CampaignMessageStatus.failed &&
            m.message_type === 'Outgoing' &&
            m.date_time_updated_at &&
            isDateInRange(
              m.date_time_updated_at,
              dateRange.startCurrent,
              dateRange.endCurrent,
            ),
        ).length,
      0,
    )

    const prevBouncedCount = _campaigns.reduce((acc, campaign) => {
      return (
        acc +
        campaign.campaign_message.filter(
          (m) =>
            m.status === CampaignMessageStatus.failed &&
            m.message_type === 'Outgoing' &&
            m.date_time_updated_at &&
            isDateInRange(
              m.date_time_updated_at,
              dateRange.startPrev,
              dateRange.endPrev,
            ),
        ).length
      )
    }, 0)

    const currentScheduledCount = _campaigns.reduce((acc, campaign) => {
      return (
        acc +
        campaign.campaign_message.filter(
          (m) =>
            m.status === CampaignMessageStatus.pending &&
            m.message_type === 'Outgoing' &&
            m.schedule_date_time &&
            isDateInRange(
              typeof m.schedule_date_time === 'string'
                ? m.schedule_date_time
                : (m.schedule_date_time as Date).toLocaleString(),
              dateRange.startCurrent,
              dateRange.endCurrent,
            ),
        ).length
      )
    }, 0)

    const prevScheduledCount = _campaigns.reduce((acc, campaign) => {
      return (
        acc +
        campaign.campaign_message.filter(
          (m) =>
            m.status === CampaignMessageStatus.pending &&
            m.message_type === 'Outgoing' &&
            m.schedule_date_time &&
            isDateInRange(
              typeof m.schedule_date_time === 'string'
                ? m.schedule_date_time
                : (m.schedule_date_time as Date).toLocaleString(),
              dateRange.startPrev,
              dateRange.endPrev,
            ),
        ).length
      )
    }, 0)

    const currentRepliesCount = _campaigns.reduce((acc, campaign) => {
      return (
        acc +
        campaign.campaign_message.filter(
          (m) =>
            m.message_type === 'Incoming' &&
            m.date_time_updated_at &&
            isDateInRange(
              m.date_time_updated_at,
              dateRange.startCurrent,
              dateRange.endCurrent,
            ),
        ).length
      )
    }, 0)

    const prevRepliesCount = _campaigns.reduce((acc, campaign) => {
      return (
        acc +
        campaign.campaign_message.filter(
          (m) =>
            m.message_type === 'Incoming' &&
            m.date_time_updated_at &&
            isDateInRange(
              m.date_time_updated_at,
              dateRange.startPrev,
              dateRange.endPrev,
            ),
        ).length
      )
    }, 0)

    const deliveredMetrics = calculatePercentageAndStatus(
      currentDeliveredCount,
      prevDeliveredCount,
    )

    const bouncedMetrics = calculatePercentageAndStatus(
      currentBouncedCount,
      prevBouncedCount,
    )

    const currentMessageSent = currentDeliveredCount + currentBouncedCount
    const prevMessageSent = prevDeliveredCount + prevBouncedCount
    const messageSentMetrics = calculatePercentageAndStatus(
      currentMessageSent,
      prevMessageSent,
    )

    const currentDeliveryRate =
      prevMessageSent > 0 && currentMessageSent !== 0
        ? Math.round((currentDeliveredCount / currentMessageSent) * 100)
        : 0
    const prevDeliveryRate =
      prevMessageSent > 0 && currentMessageSent !== 0
        ? Math.round((prevDeliveredCount / prevMessageSent) * 100)
        : 0
    const deliveryRateMetrics = {
      percentage: currentDeliveryRate - prevDeliveryRate,
      status:
        currentDeliveryRate === prevDeliveryRate
          ? StatusType.neutral
          : currentDeliveryRate > prevDeliveryRate
            ? StatusType.increase
            : StatusType.decrease,
    }

    const repliesMetrics = calculatePercentageAndStatus(
      currentRepliesCount,
      prevRepliesCount,
    )

    const scheduledMetrics = calculatePercentageAndStatus(
      currentScheduledCount,
      prevScheduledCount,
    )

    setDelivered({
      current: currentDeliveredCount,
      previous: prevDeliveredCount,
      percentage: deliveredMetrics.percentage,
      status: deliveredMetrics.status,
    })

    setBounced({
      current: currentBouncedCount,
      previous: prevBouncedCount,
      percentage: bouncedMetrics.percentage,
      status: bouncedMetrics.status,
    })

    setMessageSent({
      current: currentMessageSent,
      previous: prevMessageSent,
      percentage: messageSentMetrics.percentage,
      status: messageSentMetrics.status,
    })

    setDeliveryRate({
      current: Math.round(currentDeliveryRate * 10) / 10,
      previous: Math.round(prevDeliveryRate * 10) / 10,
      percentage: deliveryRateMetrics.percentage,
      status: deliveryRateMetrics.status,
    })

    setReplies({
      current: currentRepliesCount,
      previous: prevRepliesCount,
      percentage: repliesMetrics.percentage,
      status: repliesMetrics.status,
    })

    setScheduled({
      current: currentScheduledCount,
      previous: prevScheduledCount,
      percentage: scheduledMetrics.percentage,
      status: scheduledMetrics.status,
    })
  }

  const handleRefresh = useCallback((): void => {
    calculateCampaignOverview()
  }, [dateFilter, selectedDate, selectedCampaign, campaigns])

  const onClickBouncedAnalytics = (): void => {
    setShowBouncedModal(true)
  }

  const onDropDownSelect = (args: any): void => {
    const selectedText: string =
      args?.itemData?.text ?? args?.item?.text ?? args?.value ?? ''

    if (!selectedText || selectedText === 'Select all') {
      setSelectedCampaign({ id: '', text: 'Select all' })
      setSelectedDropdownCampaignId?.('')
      return
    }

    const matchedCampaign = campaigns.find((c) => c.name === selectedText)
    const campaignId = matchedCampaign?.campaign_id ?? ''
    setSelectedCampaign({ id: campaignId, text: selectedText })
    setSelectedDropdownCampaignId?.(campaignId)
  }

  const onHideBouncedModal = (): void => {
    setShowBouncedModal(false)
  }

  const filterSelectionList = useMemo(() => {
    return {
      campaignName: getCampaignNameList(filteredMessages),
      errorType: getErrorTypeList(filteredMessages),
    }
  }, [filteredMessages, campaigns])

  useEffect(() => {
    calculateCampaignOverview()
  }, [dateFilter, selectedDate, selectedCampaign, campaigns])

  useEffect(() => {
    return () => {
      setSelectedCampaign({
        id: '',
        text: 'Select campaign',
      })
    }
  }, [])

  return {
    bounced,
    campaignDropdownOptions,
    dateFilter,
    delivered,
    deliveryRate,
    filteredMessages,
    filterSelectionList,
    handleRefresh,
    messageSent,
    onDropDownSelect,
    onHideBouncedModal,
    replies,
    scheduled,
    selectedCampaign,
    setDateFilter,
    showBouncedModal,
    onClickBouncedAnalytics,
  }
}

export default useCampaignOverview
